import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Facebook, Twitter, Pin, Link2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, imgSrc, trackEvent } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ReviewsSection } from "@/components/Reviews";
import { toast } from "sonner";

const isVideo = (m) => /\.(mp4|webm|mov)$/i.test(m || "");

export default function ProductPage() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [activeMedia, setActiveMedia] = useState(0);
    const [variant, setVariant] = useState(null);
    const { addItem } = useCart();
    const galleryRef = useRef(null);

    const selectVariant = (v) => {
        if (v.stock === 0) return;
        setVariant(v);
        galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
        setActiveMedia(0);
        setVariant(null);
        api.get(`/products/${id}`)
            .then((res) => {
                setProduct(res.data);
                const variants = res.data.variants || [];
                if (variants.length > 0) {
                    setVariant(variants.find((v) => v.stock > 0) || variants[0]);
                }
                trackEvent({ type: "product_view", product_id: res.data.id, product_name: res.data.name });
            })
            .catch(() => setNotFound(true));
    }, [id]);

    useEffect(() => {
        setActiveMedia(0);
    }, [variant]);

    if (notFound) {
        return (
            <main className="mx-auto max-w-7xl px-6 py-32 lg:px-10" data-testid="product-not-found">
                <p className="font-serif text-3xl italic text-clay">This piece has already found a home.</p>
                <Link to="/shop" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] hover:text-crimson">
                    <ArrowLeft size={14} /> Back to the collection
                </Link>
            </main>
        );
    }

    if (!product) {
        return <main className="mx-auto max-w-7xl px-6 py-32 lg:px-10" data-testid="product-loading"><p className="font-serif text-2xl italic text-clay">Unwinding…</p></main>;
    }

    const variants = product.variants || [];
    const price = variant && variant.price != null ? variant.price : product.price;
    const stock = variant ? variant.stock : product.stock;

    const handleAdd = () => {
        if (variants.length > 0 && !variant) {
            toast.error("Choose a color first");
            return;
        }
        addItem(product, 1, variant);
        toast.success(variant ? `${product.name} — ${variant.name} added to your bag` : `${product.name} added to your bag`);
    };

    const shareUrl = window.location.href;
    const shareText = `${product.name} — handmade at Kichazone`;
    const baseMedia = product.images?.length ? product.images : [product.image].filter(Boolean);
    const media = variant?.images?.length ? variant.images : baseMedia;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied — share it anywhere");
        } catch {
            toast.error("Couldn't copy the link");
        }
    };

    return (
        <main data-testid="product-page" className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-20">
            <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors hover:text-gold">
                <ArrowLeft size={14} /> Collection
            </Link>
            <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-12">
                <motion.div
                    ref={galleryRef}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="relative scroll-mt-24 lg:col-span-7"
                    data-testid="product-gallery"
                >
                    <div className="absolute -left-4 -top-4 h-full w-full border border-espresso" aria-hidden="true" />
                    <div className="relative aspect-[3/4] overflow-hidden bg-line/40">
                        {media.length > 0 && isVideo(media[activeMedia]) ? (
                            <video key={media[activeMedia]} src={imgSrc(media[activeMedia])} controls className="h-full w-full object-cover" data-testid="product-video" />
                        ) : (
                            <img src={imgSrc(media[activeMedia] || product.image)} alt={product.name} className="h-full w-full object-cover" data-testid="product-image" />
                        )}
                        {media.length > 1 && (
                            <>
                                <button
                                    onClick={() => setActiveMedia((i) => (i - 1 + media.length) % media.length)}
                                    data-testid="gallery-prev"
                                    aria-label="Previous picture"
                                    className="absolute left-3 top-1/2 -translate-y-1/2 border border-espresso bg-sand/90 p-2.5 text-espresso backdrop-blur transition-colors duration-300 hover:bg-espresso hover:text-sand"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    onClick={() => setActiveMedia((i) => (i + 1) % media.length)}
                                    data-testid="gallery-next"
                                    aria-label="Next picture"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 border border-espresso bg-sand/90 p-2.5 text-espresso backdrop-blur transition-colors duration-300 hover:bg-espresso hover:text-sand"
                                >
                                    <ChevronRight size={16} />
                                </button>
                                <span className="absolute bottom-3 right-3 bg-espresso/80 px-2 py-1 font-mono text-[10px] tracking-[0.15em] text-sand" data-testid="gallery-counter">
                                    {activeMedia + 1} / {media.length}
                                </span>
                            </>
                        )}
                    </div>
                    {media.length > 1 && (
                        <div className="mt-4 flex flex-wrap gap-3" data-testid="media-thumbnails">
                            {media.map((m, i) => (
                                <button
                                    key={m}
                                    onClick={() => setActiveMedia(i)}
                                    data-testid={`media-thumb-${i}`}
                                    className={`h-20 w-16 overflow-hidden border transition-all duration-300 ${i === activeMedia ? "border-espresso" : "border-line opacity-60 hover:opacity-100"}`}
                                >
                                    {isVideo(m) ? (
                                        <video src={imgSrc(m)} className="h-full w-full object-cover" muted />
                                    ) : (
                                        <img src={imgSrc(m)} alt="" className="h-full w-full object-cover" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="flex flex-col lg:col-span-5"
                >
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay" data-testid="product-category">{product.category}</p>
                    <h1 className="mt-4 font-serif text-4xl font-light tracking-tighter text-gold md:text-5xl" data-testid="product-name">{product.name}</h1>
                    <p className="mt-6 font-mono text-2xl" data-testid="product-price">${price.toFixed(2)}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-clay" data-testid="product-stock">
                        {variant
                            ? stock > 0 ? `${stock} available in ${variant.name}` : `${variant.name} is sold out`
                            : stock > 0 ? `${stock} available — one of one` : "Sold out"}
                    </p>
                    {variants.length > 0 && (
                        <div className="mt-8">
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="variant-label">
                                <span className="text-clay">Color: </span>
                                <span className="text-espresso">{variant?.name}</span>
                            </p>
                            <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="variant-picker">
                                {variants.map((v) => (
                                    <button
                                        key={v.id}
                                        onClick={() => selectVariant(v)}
                                        disabled={v.stock === 0}
                                        data-testid={`variant-option-${v.id}`}
                                        className={`group w-24 shrink-0 border bg-white transition-colors duration-300 ${
                                            variant?.id === v.id
                                                ? "border-espresso"
                                                : v.stock === 0
                                                    ? "cursor-not-allowed border-line opacity-40"
                                                    : "border-line hover:border-espresso"
                                        }`}
                                    >
                                        <div className="aspect-[3/4] w-full overflow-hidden bg-line/30">
                                            <img
                                                src={imgSrc(v.image || product.image)}
                                                alt={v.name}
                                                className={`h-full w-full object-cover ${v.stock === 0 ? "grayscale" : ""}`}
                                            />
                                        </div>
                                        <p className={`truncate px-2 py-2 text-center font-mono text-[10px] uppercase tracking-[0.1em] ${
                                            variant?.id === v.id ? "text-espresso" : "text-clay"
                                        } ${v.stock === 0 ? "line-through" : ""}`}>
                                            {v.name}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="mt-8 max-w-md text-base leading-relaxed text-clay" data-testid="product-description">{product.description}</p>
                    <div className="mt-10 flex flex-col gap-3">
                        <button
                            onClick={handleAdd}
                            disabled={stock === 0}
                            data-testid="add-to-cart-button"
                            className="group flex items-center justify-center gap-3 bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Add to bag
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Share</span>
                        <a
                            href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`}
                            target="_blank" rel="noreferrer" data-testid="share-pinterest" title="Pin it"
                            className="border border-line p-2.5 transition-colors duration-300 hover:bg-espresso hover:text-sand"
                        >
                            <Pin size={14} />
                        </a>
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer" data-testid="share-facebook" title="Share on Facebook"
                            className="border border-line p-2.5 transition-colors duration-300 hover:bg-espresso hover:text-sand"
                        >
                            <Facebook size={14} />
                        </a>
                        <a
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                            target="_blank" rel="noreferrer" data-testid="share-x" title="Post on X"
                            className="border border-line p-2.5 transition-colors duration-300 hover:bg-espresso hover:text-sand"
                        >
                            <Twitter size={14} />
                        </a>
                        <button
                            onClick={copyLink}
                            data-testid="share-copy-link" title="Copy link"
                            className="border border-line p-2.5 transition-colors duration-300 hover:bg-espresso hover:text-sand"
                        >
                            <Link2 size={14} />
                        </button>
                    </div>
                    <Accordion type="single" collapsible className="mt-10">
                        <AccordionItem value="details" className="border-line">
                            <AccordionTrigger data-testid="accordion-details" className="font-mono text-xs uppercase tracking-[0.2em]">Details</AccordionTrigger>
                            <AccordionContent className="text-sm leading-relaxed text-clay">
                                Handmade or carefully selected by Kichazone — thoughtful materials, made or chosen to last. Many pieces are one of one: when it's gone, it's gone.
                                {product.size && <p className="mt-3 font-mono text-xs uppercase tracking-[0.15em]" data-testid="product-size">Size — {product.size}</p>}
                                {product.weight_oz > 0 && <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em]" data-testid="product-weight">Weight — {product.weight_oz} oz · Ships via USPS</p>}
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="shipping" className="border-line">
                            <AccordionTrigger data-testid="accordion-shipping" className="font-mono text-xs uppercase tracking-[0.2em]">Shipping</AccordionTrigger>
                            <AccordionContent className="text-sm leading-relaxed text-clay">
                                Ships within 3–5 business days, wrapped in recycled tissue with a handwritten note. Live USPS rates are calculated for your address at checkout.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="care" className="border-line">
                            <AccordionTrigger data-testid="accordion-care" className="font-mono text-xs uppercase tracking-[0.2em]">Care</AccordionTrigger>
                            <AccordionContent className="text-sm leading-relaxed text-clay">
                                Care instructions are included with every order. For handmade textiles: spot clean with cool water and mild soap, lay flat to dry. Questions? Email kichazone@gmail.com.
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </motion.div>
            </div>
            <ReviewsSection productId={product.id} />
        </main>
    );
}
